import UtilService from 'shared/services/util.service';

import { ILineItemDefaultData } from '../interface/ILineItemData';

export type LineItemDimension = 'width' | 'height' | 'depth';

export const lineItemDimensions: LineItemDimension[] = [
  'width',
  'height',
  'depth',
];

const ONE_EIGHTH_INCH = 0.125;
const ONE_QUARTER_INCH = 0.25;

// Values are saved rounded to two decimals (10.125 -> 10.13), so like the backend
// an eighth rounded to two decimals is also accepted.
const isOnStep = (value: number, step: number) =>
  Math.abs(value / step - Math.round(value / step)) < 1e-9 ||
  (step === ONE_EIGHTH_INCH &&
    UtilService.roundToTwoDecimals(Math.floor(value / step) * step) === value);

export const sortNumbers = (a: number, b: number) => a - b;

const capitalize = (dimension: LineItemDimension) =>
  `${dimension.charAt(0).toUpperCase()}${dimension.slice(1)}`;

/**
 * Mirrors the backend rule (DimUpCalculationHelper.IsWidthAllowed / IsHeightAllowed / IsDepthAllowed).
 * - Fixed checked (and valid list present): only values from the valid list are allowed.
 * - Fixed unchecked: value must be within min/max and on an allowed step.
 *   Width: 1/8" when QuarterInchWidths is checked, otherwise whole inches.
 *   Height: 1/4" when QuarterInchHeights is checked, otherwise whole inches.
 *   Depth: no step restriction.
 * Returns the error message, or null when the value is allowed.
 */
export const getDimensionRuleError = (
  dimension: LineItemDimension,
  value: number,
  data: ILineItemDefaultData | null | undefined
): string | null => {
  if (!data || Number.isNaN(value)) return null;

  const label = capitalize(dimension);

  const config = {
    width: {
      fixed: data.fixedWidths,
      validValues: data.validWidths,
      min: data.minWidth,
      max: data.maxWidth,
      step: data.quarterInchWidths ? ONE_EIGHTH_INCH : 1,
    },
    height: {
      fixed: data.fixedHeights,
      validValues: data.validHeights,
      min: data.minHeight,
      max: data.maxHeight,
      step: data.quarterInchHeights ? ONE_QUARTER_INCH : 1,
    },
    depth: {
      fixed: data.fixedDepths,
      validValues: data.validDepths,
      min: data.minDepth,
      max: data.maxDepth,
      step: null,
    },
  }[dimension];

  if (config.fixed && config.validValues?.length) {
    return config.validValues.includes(value)
      ? null
      : `Valid ${label}s: ${[...config.validValues]
          .sort(sortNumbers)
          .join(', ')}.`;
  }

  if (value < config.min || value > config.max) {
    return `${label} must be ${config.min} to ${config.max}.`;
  }

  if (config.step && !isOnStep(value, config.step)) {
    if (config.step === 1) return `${label} must be a whole number.`;
    return `${label} must be on ${
      config.step === ONE_EIGHTH_INCH ? '1/8' : '1/4'
    } inch.`;
  }

  return null;
};

export const isDimensionAllowed = (
  dimension: LineItemDimension,
  value: number,
  data: ILineItemDefaultData | null | undefined
) => getDimensionRuleError(dimension, value, data) === null;
